import { useCallback, useEffect, useRef, useState } from "react";
import {
  apiFetchWorkspaceTopics,
  apiPinWorkspaceTopic,
  apiUnpinWorkspaceTopic,
  type WorkspaceTopicItem,
} from "../lib/authApi";

const DEFAULT_PAGE_SIZE = 50;

function cmpTopics(a: WorkspaceTopicItem, b: WorkspaceTopicItem): number {
  const ta = a.created_at - b.created_at;
  if (ta !== 0) return ta;
  return a.id.localeCompare(b.id);
}

type UseWorkspaceTopicsArgs = {
  enabled: boolean;
  workspaceId: string;
  topicsRefreshEpoch?: number;
  pageSize?: number;
  ensureTopicId?: string | null;
  onPinnedTopicIdsChange?: (workspaceId: string, ids: string[]) => void;
};

export function useWorkspaceTopics({
  enabled,
  workspaceId,
  topicsRefreshEpoch = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  ensureTopicId = null,
  onPinnedTopicIdsChange,
}: UseWorkspaceTopicsArgs) {
  const [topics, setTopics] = useState<WorkspaceTopicItem[]>([]);
  const [pinnedTopicIds, setPinnedTopicIds] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentWorkspaceIdRef = useRef<string | null>(null);
  const hasLoadedForWorkspaceRef = useRef(false);

  useEffect(() => {
    if (onPinnedTopicIdsChange && workspaceId) {
      onPinnedTopicIdsChange(workspaceId, pinnedTopicIds);
    }
  }, [onPinnedTopicIdsChange, workspaceId, pinnedTopicIds]);

  useEffect(() => {
    if (!enabled || !workspaceId) {
      currentWorkspaceIdRef.current = null;
      hasLoadedForWorkspaceRef.current = false;
      setTopics([]);
      setPinnedTopicIds([]);
      setNextCursor(null);
      setHasMore(false);
      setInitialLoading(false);
      setRefreshing(false);
      setError(null);
      return;
    }
    const workspaceChanged = currentWorkspaceIdRef.current !== workspaceId;
    if (workspaceChanged) {
      currentWorkspaceIdRef.current = workspaceId;
      hasLoadedForWorkspaceRef.current = false;
      setTopics([]);
      setPinnedTopicIds([]);
      setNextCursor(null);
      setHasMore(false);
    }
    let cancelled = false;
    if (hasLoadedForWorkspaceRef.current) {
      setRefreshing(true);
    } else {
      setInitialLoading(true);
    }
    setError(null);
    void apiFetchWorkspaceTopics(workspaceId, {
      limit: pageSize,
      ensureTopicId: ensureTopicId ?? undefined,
    })
      .then((d) => {
        if (cancelled) return;
        setTopics(d.topics);
        setPinnedTopicIds(d.pinned_topic_ids ?? []);
        setHasMore(d.has_more ?? false);
        setNextCursor(d.next_cursor ?? null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setTopics([]);
        setPinnedTopicIds([]);
        setNextCursor(null);
        setHasMore(false);
      })
      .finally(() => {
        if (cancelled) return;
        hasLoadedForWorkspaceRef.current = true;
        setInitialLoading(false);
        setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId, topicsRefreshEpoch, pageSize, ensureTopicId]);

  const loadMore = useCallback(async () => {
    if (!workspaceId || !hasMore || nextCursor == null || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const d = await apiFetchWorkspaceTopics(workspaceId, {
        limit: pageSize,
        cursor: nextCursor,
      });
      setTopics((prev) => {
        const seen = new Set(prev.map((t) => t.id));
        const merged = [...prev];
        for (const t of d.topics) {
          if (!seen.has(t.id)) {
            seen.add(t.id);
            merged.push(t);
          }
        }
        return merged.sort(cmpTopics);
      });
      if (d.pinned_topic_ids) setPinnedTopicIds(d.pinned_topic_ids);
      setHasMore(d.has_more ?? false);
      setNextCursor(d.next_cursor ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }, [workspaceId, hasMore, nextCursor, loadingMore, pageSize]);

  const addLocalTopic = useCallback((topic: WorkspaceTopicItem) => {
    setTopics((prev) => {
      if (prev.some((t) => t.id === topic.id)) return prev;
      return [...prev, topic].sort(cmpTopics);
    });
  }, []);

  const togglePinnedTopic = useCallback(
    async (topicId: string, pin: boolean) => {
      if (pin) {
        await apiPinWorkspaceTopic(workspaceId, topicId);
        setPinnedTopicIds((prev) => (prev.includes(topicId) ? prev : [...prev, topicId]));
      } else {
        await apiUnpinWorkspaceTopic(workspaceId, topicId);
        setPinnedTopicIds((prev) => prev.filter((id) => id !== topicId));
      }
    },
    [workspaceId],
  );

  return {
    topics,
    pinnedTopicIds,
    loading: initialLoading,
    initialLoading,
    refreshing,
    loadingMore,
    error,
    hasMore,
    loadMore,
    addLocalTopic,
    togglePinnedTopic,
  };
}
